/**
 * AssistantChatService — conversation state for the floating assistant chat
 * widget.
 *
 * Owns one chat, its streaming lifecycle and its conversationId. The chat
 * belongs to one {@link AssistantScope} (a network or a Frontdoor account).
 * Arriving in another scope drops it and clears its server-side memory. A page
 * with no scope keeps it. Input history for arrow-up/down recall is kept across
 * scopes. Chat and history persist to sessionStorage, so a reload keeps them.
 *
 * Replies stream in over SSE: `message` events append text fragments to the
 * pending assistant message, and `chart` and `table` events attach charts and
 * tables to it. Each arrives before the text that references its
 * `[chart:<id>]` or `[table:<id>]` marker, so inline placement resolves as the
 * text arrives. A `question` event attaches a clarifying question. The answer
 * goes with the next send.
 *
 * A `changed` event means a tool may have changed data. The listing on screen
 * then reloads, as if the user had pressed the table's refresh button.
 *
 * The first request omits the conversationId; the server generates one and
 * returns it in the `X-Conversation-Id` response header. We adopt that id
 * and send it on every subsequent request.
 */

import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DataTableFilterService } from 'ziti-console-lib';
import { TokenService } from '@netfoundry-ui/shared/services';
import {
    AssistantService,
    AssistantChart,
    AssistantHttpError,
    AssistantTable,
    ClarifyingQuestion,
    QuestionAnswer,
} from './api/assistant.service';
import { AiConfigService } from './api/assistant-config.service';
import { ASSISTANT_SUBJECTS, ASSISTANT_TITLES, AssistantScope, assistantScopeKey } from './api/assistant-scope';
import { AssistantScopeService } from './assistant-scope.service';

const STORAGE_KEY = 'cloudziti.assistantChat.v3';

/** Blobs of earlier storage formats. Removed on restore. */
const LEGACY_STORAGE_KEYS = ['cloudziti.assistantChat.v1', 'cloudziti.assistantChat.v2'];

/** Input-history entries kept for arrow-up/down recall. */
const MAX_SENT_HISTORY = 50;

/** A reply can change several things in a row. The listing reloads once, this long after the last change. */
export const RELOAD_DELAY_MS = 300;

export interface AssistantChatMessage {
    role: 'user' | 'assistant';
    text: string;
    /** Charts attached to this reply; rendered inline at their marker, or after the text when unreferenced. */
    charts?: AssistantChart[];
    /** Tables attached to this reply; rendered inline at their marker, or after the text when unreferenced. */
    tables?: AssistantTable[];
    /** The question this reply asks back, with its suggested answers. */
    question?: ClarifyingQuestion;
    /** Assistant message currently waiting for (the rest of) its reply. */
    pending?: boolean;
    /** The request behind this assistant message failed. */
    error?: boolean;
}

/** The conversation in one scope. */
interface ScopeChat {
    scope: AssistantScope;
    conversationId: string | null;
    messages: AssistantChatMessage[];
}

interface PersistedChat {
    chat: ScopeChat | null;
    sentHistory: string[];
}

@Injectable({ providedIn: 'root' })
export class AssistantChatService {
    /** The scope the console is on, or null when it offers no assistant. */
    readonly scope = inject(AssistantScopeService).scope;

    /** Title for the panel and launcher: which assistant the user is talking to. */
    readonly title = computed(() => ASSISTANT_TITLES[this.scope()?.kind ?? 'networks']);

    /** What the assistant works on, for running text such as the empty-state hint. */
    readonly subject = computed(() => ASSISTANT_SUBJECTS[this.scope()?.kind ?? 'networks']);

    private readonly _chat = signal<ScopeChat | null>(null);

    /** Sent-message history for arrow-up/down recall (oldest first). It is kept across scopes and new conversations. */
    private readonly _sentHistory = signal<string[]>([]);

    private readonly activeKey = computed(() => {
        const scope = this.scope();
        return scope ? assistantScopeKey(scope) : '';
    });

    readonly messages = computed(() => {
        const chat = this._chat();
        return chat && assistantScopeKey(chat.scope) === this.activeKey() ? chat.messages : [];
    });

    private _isOpen = signal(false);
    readonly isOpen = this._isOpen.asReadonly();

    /** A reply is streaming in. */
    private _isStreaming = signal(false);
    readonly isStreaming = this._isStreaming.asReadonly();

    /** Enlarged panel mode, toggled from the panel header. */
    private _isExpanded = signal(false);
    readonly isExpanded = this._isExpanded.asReadonly();

    /**
     * The assistant can take a question: the console is on a scope whose network has the AI_ASSISTANT
     * extension, and the tenant switched the assistant on in its settings. Unknown counts as off, so
     * nothing offers the assistant before the config read answers.
     */
    readonly available = computed(() => {
        const scope = this.scope();
        return !!scope && this.aiConfig.enabledFor(scope) === true;
    });

    /** The transcript the in-flight stream writes to. Fixed at send, so a scope switch mid-reply cannot misfile it. */
    private streamKey = '';
    /** Aborts the in-flight stream, so it can be canceled from stop(). */
    private abortController: AbortController | null = null;

    private readonly filterService = inject(DataTableFilterService);
    private readonly changes = new Subject<void>();

    constructor(
        private api: AssistantService,
        private tokenService: TokenService,
        private aiConfig: AiConfigService
    ) {
        this.restore();
        // Leaving a scope stops its reply, and arriving in another scope starts a fresh chat.
        // A page with no scope keeps the chat, so a visit to a v1/v2 Frontdoor page loses nothing.
        effect(() => {
            const key = this.activeKey();
            untracked(() => {
                if (this.streamKey && this.streamKey !== key) {
                    this.stop();
                }
                this.discardOtherScope(key);
            });
        });
        this.changes.pipe(debounceTime(RELOAD_DELAY_MS), takeUntilDestroyed()).subscribe(() => this.reloadListing());
    }

    /** Messages the user has sent, oldest first — shell-style input history for arrow-up/down recall. */
    get sentHistory(): readonly string[] {
        return this._sentHistory();
    }

    toggleOpen(): void {
        this._isOpen.set(!this._isOpen());
    }

    open(): void {
        this._isOpen.set(true);
    }

    /**
     * Open the panel and send a question on the user's behalf. Does nothing while the assistant is off or a reply
     * streams.
     */
    ask(text: string): Promise<void> {
        if (!this.available() || this._isStreaming()) {
            return Promise.resolve();
        }
        this.open();
        return this.send(text);
    }

    close(): void {
        this._isOpen.set(false);
    }

    toggleExpanded(): void {
        this._isExpanded.set(!this._isExpanded());
    }

    /**
     * Send a user message and stream the assistant's reply into the active scope's transcript. A typed answer
     * to the previous reply's question travels with it; the transcript shows only the text.
     */
    async send(text: string, answer?: QuestionAnswer): Promise<void> {
        const message = text.trim();
        const scope = this.scope();
        if (!message || !scope || this._isStreaming()) {
            return;
        }
        const key = assistantScopeKey(scope);
        // The effect has not run yet when a send follows a scope change in the same tick.
        this.discardOtherScope(key);
        const chat = this._chat() ?? { scope, conversationId: null, messages: [] };
        const conversationId = chat.conversationId;
        // The conversation id can arrive after the chat was dropped, so the stream keeps its own copy for the clear in
        // finally.
        let streamConversationId = conversationId;
        this._chat.set({
            ...chat,
            messages: [
                ...chat.messages,
                { role: 'user', text: message },
                { role: 'assistant', text: '', pending: true },
            ],
        });
        this._sentHistory.update((history) => withSentMessage(history, message));
        this.streamKey = key;
        this._isStreaming.set(true);
        this.persist();

        this.abortController = new AbortController();
        try {
            await this.api.streamChat(
                scope,
                // Omit conversationId on the first exchange — the server generates
                // one and returns it in the X-Conversation-Id header.
                { message, ...(conversationId ? { conversationId } : {}), ...(answer ? { answer } : {}) },
                {
                    onConversationId: (id) => {
                        streamConversationId = id;
                        this.updateChat(key, (current) => ({ ...current, conversationId: id }));
                    },
                    onDelta: (fragment) =>
                        this.updatePendingReply(key, (last) => ({ ...last, text: last.text + fragment })),
                    onChart: (chart) =>
                        this.updatePendingReply(key, (last) => ({ ...last, charts: [...(last.charts ?? []), chart] })),
                    onChanged: () => this.changes.next(),
                    onTable: (table) =>
                        this.updatePendingReply(key, (last) => ({ ...last, tables: [...(last.tables ?? []), table] })),
                    onQuestion: (question) => this.updatePendingReply(key, (last) => ({ ...last, question })),
                },
                this.abortController.signal
            );
            this.finishReply(key);
        } catch (err) {
            if (this.isAbort(err)) {
                // Stopped by the user or by a scope switch — keep whatever streamed in as a finished turn.
                this.finishReply(key);
            } else if (err instanceof AssistantHttpError && err.status === 401) {
                // The streaming fetch bypasses the HTTP interceptors, so an expired
                // session must trigger the same forced logout here that every
                // HttpClient call gets.
                this.failReply(key);
                this.tokenService.logoutSource.next(true);
            } else {
                console.error('[assistant-chat] chat request failed:', err);
                this.failReply(key);
            }
        } finally {
            // A chat dropped mid-reply is cleared on the server only now, after the server has stored the reply.
            const current = this._chat();
            if (!current || assistantScopeKey(current.scope) !== key) {
                this.clearOnServer(scope, streamConversationId);
            }
            this.settle();
        }
    }

    /** Cancel an in-flight stream, leaving the partial reply as a finished turn. */
    stop(): void {
        this.abortController?.abort();
    }

    /** Start over: clear the server-side memory (best effort) and the local transcript. Input history is kept. */
    newConversation(): void {
        if (!this.scope()) {
            return;
        }
        this.stop();
        this.discard();
    }

    /**
     * Reloads the listing on screen the way the table's refresh button does: re-emit the current page. Every list page
     * reloads on that and keeps its filters, sort and page. A page without a listing does not listen, so nothing
     * happens.
     */
    private reloadListing(): void {
        this.filterService.changePage(this.filterService.currentPage);
    }

    private isAbort(err: unknown): boolean {
        return err instanceof DOMException && err.name === 'AbortError';
    }

    private settle(): void {
        this._isStreaming.set(false);
        this.abortController = null;
        this.streamKey = '';
        this.persist();
    }

    /** The stream ended (normally or stopped) — the pending assistant message is complete as it stands. */
    private finishReply(key: string): void {
        this.updatePendingReply(key, (last) => ({ ...last, pending: false }));
    }

    private failReply(key: string): void {
        this.updatePendingReply(key, (last) => ({ ...last, pending: false, error: true }));
    }

    private updatePendingReply(key: string, change: (last: AssistantChatMessage) => AssistantChatMessage): void {
        this.updateChat(key, (chat) => {
            const last = chat.messages[chat.messages.length - 1];
            if (!last || last.role !== 'assistant') {
                return chat;
            }
            return { ...chat, messages: [...chat.messages.slice(0, -1), change(last)] };
        });
    }

    /** Drop the chat when it belongs to a scope other than `key`. An empty key (no scope on screen) drops nothing. */
    private discardOtherScope(key: string): void {
        const chat = this._chat();
        if (key && chat && assistantScopeKey(chat.scope) !== key) {
            this.discard();
        }
    }

    /**
     * Drop the chat and clear its server-side memory, best effort. A chat with a reply in flight is cleared when the
     * stream ends.
     */
    private discard(): void {
        const chat = this._chat();
        if (chat && this.streamKey !== assistantScopeKey(chat.scope)) {
            this.clearOnServer(chat.scope, chat.conversationId);
        }
        this._chat.set(null);
        this.persist();
    }

    private clearOnServer(scope: AssistantScope, conversationId: string | null): void {
        if (!conversationId) {
            return;
        }
        firstValueFrom(this.api.clearConversation(scope, conversationId)).catch(() => {
            /* best effort — memory expires server-side regardless */
        });
    }

    /** Change the chat of scope `key`. A stream that ends after its chat was dropped changes nothing. */
    private updateChat(key: string, change: (chat: ScopeChat) => ScopeChat): void {
        this._chat.update((chat) => (chat && assistantScopeKey(chat.scope) === key ? change(chat) : chat));
    }

    private persist(): void {
        try {
            const chat = this._chat();
            // Don't persist an in-flight pending flag — on restore there is no
            // request to resume, so the message is whatever landed.
            const data: PersistedChat = {
                chat: chat && {
                    ...chat,
                    messages: chat.messages.map((m) => ({
                        role: m.role,
                        text: m.text,
                        charts: m.charts,
                        tables: m.tables,
                        question: m.question,
                        error: m.error,
                    })),
                },
                sentHistory: this._sentHistory(),
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch {
            /* storage unavailable — non-fatal */
        }
    }

    private restore(): void {
        try {
            LEGACY_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
            const raw = sessionStorage.getItem(STORAGE_KEY);
            if (!raw) {
                return;
            }
            const data = JSON.parse(raw) as PersistedChat;
            const chat = data?.chat;
            if (chat?.scope?.kind && chat.scope.id && Array.isArray(chat.messages)) {
                this._chat.set({
                    scope: chat.scope,
                    conversationId: chat.conversationId ?? null,
                    messages: chat.messages,
                });
            }
            if (Array.isArray(data?.sentHistory)) {
                this._sentHistory.set(data.sentHistory);
            }
        } catch {
            /* corrupt blob — start fresh */
        }
    }
}

/** Append to the input history, skipping a consecutive duplicate (shell behavior) and capping the size. */
function withSentMessage(history: string[], message: string): string[] {
    if (history[history.length - 1] === message) {
        return history;
    }
    return [...history, message].slice(-MAX_SENT_HISTORY);
}
